import { createSlice } from '@reduxjs/toolkit';

const DEFAULT_WHOLESALE_CONFIG = {
  isActive: false,
  minimumPurchaseQuantity: 4,
  wholesaleMinQuantity: 10,
  minQuantity: 10,
  wholesaleOnlyMode: false
};

const roundPrice = (value) => Math.round((Number(value) || 0) * 100) / 100;

const getCartQuantity = (items) => items.reduce((acc, item) => acc + (Number(item.quantity) || 0), 0);

const getWholesalePrice = (item) => {
  const regularPrice = Number(item.regularPrice ?? item.price) || 0;
  const wholesalePrice = Number(item.wholesalePrice) || 0;

  if (wholesalePrice <= 0) {
    return regularPrice;
  }

  return roundPrice(wholesalePrice);
};

const recalculateCart = (state) => {
  const config = state.wholesaleConfig || DEFAULT_WHOLESALE_CONFIG;
  const totalQuantity = getCartQuantity(state.items);
  const wholesaleMinQuantity = Number(config.wholesaleMinQuantity) || DEFAULT_WHOLESALE_CONFIG.wholesaleMinQuantity;
  const shouldApplyWholesale = config.isActive && totalQuantity >= wholesaleMinQuantity;

  state.items.forEach((item) => {
    const regularPrice = Number(item.regularPrice ?? item.price) || 0;
    item.regularPrice = regularPrice;
    item.price = shouldApplyWholesale ? getWholesalePrice(item) : regularPrice;
    item.isWholesalePrice = shouldApplyWholesale && Number(item.wholesalePrice) > 0 && item.price < regularPrice;
  });

  state.total = roundPrice(
    state.items.reduce((acc, item) => acc + ((Number(item.price) || 0) * (Number(item.quantity) || 0)), 0)
  );
};

// Generate a unique cart key for each product + variant combination
const getCartKey = (item) => {
  if (item.selectedVariant?.sku) return `${item._id}-${item.selectedVariant.sku}`;
  if (item.selectedVariant?.size || item.selectedVariant?.color) {
    return `${item._id}-${item.selectedVariant.size || ''}-${item.selectedVariant.color || ''}`;
  }
  if (item.variantInfo) return `${item._id}-${item.variantInfo}`;
  if (item.selectedSizes?.length || item.selectedColors?.length) {
    return `${item._id}-${(item.selectedSizes || []).join(',')}-${(item.selectedColors || []).join(',')}`;
  }
  return item._id;
};

const cartSlice = createSlice({
  name: 'cart',
  initialState: { items: [], total: 0, wholesaleConfig: DEFAULT_WHOLESALE_CONFIG },
  reducers: {
    addToCart: (state, action) => {
      const newQuantity = action.payload.quantity || 1;
      const newKey = getCartKey(action.payload);
      const stockLimit = action.payload.stockLimit;
      const existingItem = state.items.find(item => getCartKey(item) === newKey);
      if (existingItem) {
        const combined = existingItem.quantity + newQuantity;
        existingItem.quantity = stockLimit != null ? Math.min(combined, stockLimit) : combined;
        existingItem.regularPrice = Number(action.payload.regularPrice ?? action.payload.price ?? existingItem.regularPrice) || 0;
        existingItem.wholesalePrice = Number(action.payload.wholesalePrice ?? existingItem.wholesalePrice) || 0;
        if (stockLimit != null) existingItem.stockLimit = stockLimit;
      } else {
        state.items.push({
          ...action.payload,
          cartKey: newKey,
          regularPrice: Number(action.payload.regularPrice ?? action.payload.price) || 0,
          wholesalePrice: Number(action.payload.wholesalePrice) || 0,
          quantity: stockLimit != null ? Math.min(newQuantity, stockLimit) : newQuantity,
          stockLimit: stockLimit ?? undefined
        });
      }
      recalculateCart(state);
    },    
    removeFromCart: (state, action) => {
      const key = getCartKey(action.payload);
      const itemIndex = state.items.findIndex(item => getCartKey(item) === key);
      if (itemIndex !== -1) {
        const item = state.items[itemIndex];
        if (item.quantity > 1) {
          item.quantity -= 1;
        } else {
          state.items.splice(itemIndex, 1);
        }
        recalculateCart(state);
      }
    },
    deleteFromCart: (state, action) => {
      const key = getCartKey(action.payload);
      const itemIndex = state.items.findIndex(item => getCartKey(item) === key);
      if (itemIndex !== -1) {
        state.items.splice(itemIndex, 1);
        recalculateCart(state);
      }
    },
    clearCart: (state) => {
      state.items = [];
      state.total = 0;
    },
    updateStockValidation: (state, action) => {
      const issues = action.payload?.issues || [];
      const availableItems = action.payload?.items || [];

      state.items.forEach((item) => {
        const productId = item._id?.toString();
        const variantId = item.selectedVariant?.variantId?.toString();
        const issue = issues.find((entry) => (
          entry.productId === productId && (!entry.variantId || entry.variantId === variantId)
        ));
        const available = availableItems.find((entry) => (
          entry.productId === productId && (!variantId || entry.variantId === variantId)
        ));

        item.stockIssue = issue || undefined;
        if (available) item.stockLimit = available.available ?? undefined;
      });
    },
    setWholesaleConfig: (state, action) => {
      const minimumPurchaseQuantity = Math.max(
        1,
        Number(action.payload?.minimumPurchaseQuantity) || DEFAULT_WHOLESALE_CONFIG.minimumPurchaseQuantity
      );
      const wholesaleMinQuantity = Math.max(
        minimumPurchaseQuantity,
        Number(action.payload?.wholesaleMinQuantity) || Number(action.payload?.minQuantity) || DEFAULT_WHOLESALE_CONFIG.wholesaleMinQuantity
      );

      state.wholesaleConfig = {
        isActive: action.payload?.isActive === true,
        minimumPurchaseQuantity,
        wholesaleMinQuantity,
        minQuantity: wholesaleMinQuantity,
        wholesaleOnlyMode: false
      };
      recalculateCart(state);
    }    
  }
});

export const { addToCart, removeFromCart, deleteFromCart, clearCart, updateStockValidation, setWholesaleConfig } = cartSlice.actions;
export default cartSlice.reducer;
